import { useMemo } from 'react';
import {
  IBudgetFormData,
  IBudgetService,
  IBudgetItem,
  IClientData,
  IVehicleData,
  IFileItem,
} from '../types';

interface IUseFormUpdatedParams {
  isEditMode: boolean;
  initialData?: IBudgetFormData;
  services: IBudgetService[];
  recipient: string | undefined;
  vehicleData: IVehicleData | undefined;
  clientData: IClientData | undefined;
  counterProposalValue: number | undefined;
  counterProposalObs: string | undefined;
  files: IFileItem[];
}

const normalizeItem = (item: IBudgetItem): Omit<IBudgetItem, 'ref' | 'itemOptions' | 'quantityOptions' | 'removed' | 'subtotal'> => {
  const { ref, itemOptions, quantityOptions, removed, subtotal, ...normalized } = item;
  return normalized;
};

const normalizeService = (service: IBudgetService): Omit<IBudgetService, 'id'> => {
  const { id, clientPercentage, volkstotalPercentage, dealershipPercentage, warrantyPercentage, percentages, ...rest } = service;
  return {
    ...rest,
    items: service.items
      .filter((item) => !item.removed)
      .map((item) => normalizeItem(item) as IBudgetItem),
  };
};

const compareServices = (services1: IBudgetService[], services2: IBudgetService[]): boolean => {
  if (services1.length !== services2.length) {
    return false;
  }

  const normalized1 = services1.map(normalizeService);
  const normalized2 = services2.map(normalizeService);

  return JSON.stringify(normalized1) === JSON.stringify(normalized2);
};

export const useFormUpdated = (params: IUseFormUpdatedParams): boolean => {
  const {
    isEditMode,
    initialData,
    services,
    recipient,
    vehicleData,
    clientData,
    counterProposalValue,
    counterProposalObs,
    files,
  } = params;

  const isFormUpdated = useMemo(() => {
    if (!isEditMode || !initialData) {
      return false;
    }

    const initialServices = initialData.services || [];
    const currentServices = services || [];
    if (!compareServices(initialServices, currentServices)) {
      return true;
    }

    if (initialData.recipient !== recipient) {
      return true;
    }

    if (JSON.stringify(initialData.vehicleData) !== JSON.stringify(vehicleData)) {
      return true;
    }

    if (JSON.stringify(initialData.clientData) !== JSON.stringify(clientData)) {
      return true;
    }

    if (initialData.counterProposalValue !== counterProposalValue) {
      return true;
    }

    if (initialData.counterProposalObs !== counterProposalObs) {
      return true;
    }

    const hasNewFiles = files.some((file) => file.file !== undefined);
    if (hasNewFiles) {
      return true;
    }

    return false;
  }, [
    isEditMode,
    initialData,
    services,
    recipient,
    vehicleData,
    clientData,
    counterProposalValue,
    counterProposalObs,
    files,
  ]);

  return isFormUpdated;
};

